import i18n from "@/i18n";
/**
 * AddEmployeeSheet — Prompt 10B §19-§37.
 *
 * Right-side shadcn Sheet used to add an Employee to the active
 * Location. Frozen field list, frozen enums, frozen payload shape.
 * The panel is Location-contextual: the current MiseOne
 * restaurantId/currentRestaurant is the ONLY authorized Main Location
 * and it is submitted verbatim in `restaurant_id`. If restaurantId
 * changes while the panel is open the caller must close the panel and
 * discard the form (WorkforceTeam owns that lifecycle rule).
 *
 * Contract:
 *   POST /api/workforce/employees  (Prompt 9B).
 *   Response 201 → parent triggers Team refetch. No optimistic row.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import {
    EMPLOYEE_NUMBER_TAKEN_MESSAGE, EmployeeNumberInput,
    isEmployeeNumberTaken,
} from "./EmployeeNumberInput";
import {
    browserLocalYMD, VV_SYSTEM_OPTIONS, VV_UNSUPPORTED_SYSTEMS,
} from "./workforceHelpers";

// ─── Pure helpers (exported for tests) ──────────────────────
export function toCanonicalMinutes({ hours, minutes }) {
    const h = Number.isInteger(hours) ? hours : 0;
    const m = Number.isInteger(minutes) ? minutes : 0;
    return h * 60 + m;
}

export function buildAddEmployeePayload({
    restaurantId, firstName, lastName, mainRole,
    contractType, hours, minutes, workingTimeModel, tesFamily,
    payrollIncluded, startDate, endDate, workerType, vvSystem,
    employeeNumber,
}) {
    const body = {
        restaurant_id: restaurantId,
        first_name: (firstName ?? "").trim(),
        last_name: (lastName ?? "").trim(),
        main_role: (mainRole ?? "").trim(),
        contract_type: contractType,
        contract_hours_minutes: toCanonicalMinutes({ hours, minutes }),
        contract_period_unit: "THREE_WEEKS",
        working_time_model: workingTimeModel,
        tes_family: tesFamily,
        payroll_included: Boolean(payrollIncluded),
        start_date: startDate,
        end_date: endDate ? endDate : null,
        // Prompt 20A §1 · §2 · §3 · §18 — canonical worker classification
        // (`DIRECT` = regular employee, `EXTRA` = extra / temporary).
        worker_type: workerType || "DIRECT",
    };
    // TES-B2C1B1 §7 — never default; omitted stays "not set".
    if (vvSystem) body.vv_system = vvSystem;
    // PAYROLL-FINAL-B2 — optional string; leading zeros preserved.
    const number = (employeeNumber ?? "").trim();
    if (number) body.employee_number = number;
    return body;
}

export function isFormComplete(state) {
    if (!(state.firstName ?? "").trim()) return false;
    if (!(state.lastName ?? "").trim()) return false;
    if (!(state.mainRole ?? "").trim()) return false;
    if (state.contractType !== "FULL_TIME"
            && state.contractType !== "PART_TIME") return false;
    if (!Number.isInteger(state.hours) || state.hours < 0) return false;
    if (!Number.isInteger(state.minutes)
            || state.minutes < 0 || state.minutes > 59) return false;
    if (state.workingTimeModel !== "STANDARD"
            && state.workingTimeModel !== "AVERAGING") return false;
    if (state.tesFamily !== "PAM_MARA") return false;
    if (!state.startDate) return false;
    if (state.endDate && state.endDate < state.startDate) return false;
    return true;
}

const initialState = () => ({
    firstName: "", lastName: "", mainRole: "",
    contractType: "",  // manager must choose
    hours: 0, minutes: 0,
    workingTimeModel: "STANDARD",
    tesFamily: "PAM_MARA",
    payrollIncluded: true,
    startDate: browserLocalYMD(),
    endDate: "",
    // Prompt 20A §3 · §18 — default `Regular employee` (DIRECT).
    workerType: "DIRECT",
    vvSystem: "",
    employeeNumber: "",
});

export default function AddEmployeeSheet({
    open, onOpenChange, restaurantId, restaurantName, onCreated,
}) {
    const [state, setState] = useState(initialState);
    const [pending, setPending] = useState(false);

    // Reset the form every time the Sheet opens.
    useEffect(() => {
        if (open) setState(initialState());
    }, [open]);

    const set = (patch) => setState((s) => ({ ...s, ...patch }));

    const parseInt0 = (raw) => {
        if (raw === "" || raw === null || raw === undefined) return 0;
        const n = Number(raw);
        return Number.isInteger(n) ? n : NaN;
    };

    const submit = async () => {
        if (pending) return;
        if (!isFormComplete(state)) return;
        setPending(true);
        try {
            const body = buildAddEmployeePayload({
                restaurantId,
                firstName: state.firstName,
                lastName: state.lastName,
                mainRole: state.mainRole,
                contractType: state.contractType,
                hours: state.hours,
                minutes: state.minutes,
                workingTimeModel: state.workingTimeModel,
                tesFamily: state.tesFamily,
                payrollIncluded: state.payrollIncluded,
                startDate: state.startDate,
                endDate: state.endDate,
                workerType: state.workerType,
                vvSystem: state.vvSystem,
                employeeNumber: state.employeeNumber,
            });
            await api.post("/workforce/employees", body);
            toast.success(i18n.t("wf.addEmployeeSheet.employeeAdded", "Employee added"));
            onOpenChange(false);
            setState(initialState());
            onCreated?.();
        } catch (err) {
            const status = err?.response?.status;
            if (isEmployeeNumberTaken(err)) {
                toast.error(i18n.t("wf.EmployeeNumberInput.EMPLOYEE_NUMBER_TAKEN_MESSAGE", EMPLOYEE_NUMBER_TAKEN_MESSAGE));
            } else if (status === 422) {
                toast.error(i18n.t("wf.addEmployeeSheet.checkTheEmployeeInformationAndTry", "Check the employee information and try again."));
            } else if (status === 403) {
                toast.error(i18n.t("wf.addEmployeeSheet.youCannotAddAnEmployeeIn", "You cannot add an employee in this location."));
            } else {
                toast.error(i18n.t("wf.addEmployeeSheet.couldNotAddEmployeeTryAgain", "Could not add employee. Try again."));
            }
        } finally {
            setPending(false);
        }
    };

    const canSubmit = !pending && isFormComplete(state);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                data-testid="add-employee-sheet"
                className="w-full sm:max-w-md overflow-y-auto"
            >
                <SheetHeader>
                    <SheetTitle>{i18n.t("wf.addEmployeeSheet.addEmployee", "Add Employee")}</SheetTitle>
                </SheetHeader>

                <div className="mt-4 space-y-4">
                    {/* First / Last name */}
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block text-sm">
                            <span className="text-neutral-700">{i18n.t("wf.addEmployeeSheet.firstName", "First Name")}</span>
                            <Input
                                data-testid="add-employee-first-name"
                                value={state.firstName}
                                onChange={(e) =>
                                    set({ firstName: e.target.value })}
                            />
                        </label>
                        <label className="block text-sm">
                            <span className="text-neutral-700">{i18n.t("wf.addEmployeeSheet.lastName", "Last Name")}</span>
                            <Input
                                data-testid="add-employee-last-name"
                                value={state.lastName}
                                onChange={(e) =>
                                    set({ lastName: e.target.value })}
                            />
                        </label>
                    </div>

                    <EmployeeNumberInput
                        testId="add-employee-number"
                        value={state.employeeNumber}
                        onChange={(v) => set({ employeeNumber: v })}
                    />

                    {/* Main Location — read-only */}
                    <div className="text-sm">
                        <span className="text-neutral-700">{i18n.t("wf.addEmployeeSheet.mainLocation", "Main Location")}</span>
                        <div
                            data-testid="add-employee-main-location"
                            className="mt-1 rounded-md border border-neutral-200
                                        bg-neutral-50 px-3 py-2 text-neutral-900"
                        >
                            {restaurantName || "\u2014"}
                        </div>
                    </div>

                    {/* Main Role */}
                    <label className="block text-sm">
                        <span className="text-neutral-700">{i18n.t("wf.addEmployeeSheet.mainRole", "Main Role")}</span>
                        <Input
                            data-testid="add-employee-main-role"
                            value={state.mainRole}
                            onChange={(e) => set({ mainRole: e.target.value })}
                        />
                    </label>

                    {/* Prompt 20A §3 · §18 — Worker type. Default
                        Regular employee (DIRECT); EXTRA marks an
                        occasional/temporary worker. Additive field. */}
                    <div className="text-sm">
                        <span className="text-neutral-700">{i18n.t("wf.addEmployeeSheet.workerType", "Worker type")}</span>
                        <Select
                            value={state.workerType}
                            onValueChange={(v) => set({ workerType: v })}
                        >
                            <SelectTrigger
                                data-testid="add-employee-worker-type">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="DIRECT"
                                                data-testid="opt-direct">
                                    {i18n.t("wf.addEmployeeSheet.regularEmployee", "Regular employee")}
                                </SelectItem>
                                <SelectItem value="EXTRA"
                                                data-testid="opt-extra">
                                    {i18n.t("wf.addEmployeeSheet.extraTemporary", "Extra / temporary")}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Contract Type */}
                    <div className="text-sm">
                        <span className="text-neutral-700">{i18n.t("wf.addEmployeeSheet.contractType", "Contract Type")}</span>
                        <Select
                            value={state.contractType}
                            onValueChange={(v) => set({ contractType: v })}
                        >
                            <SelectTrigger
                                data-testid="add-employee-contract-type">
                                <SelectValue placeholder={i18n.t("wf.addEmployeeSheet.select", "Select…")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="FULL_TIME"
                                                data-testid="opt-full-time">
                                    {i18n.t("wf.addEmployeeSheet.fullTime", "Full-time")}
                                </SelectItem>
                                <SelectItem value="PART_TIME"
                                                data-testid="opt-part-time">
                                    {i18n.t("wf.addEmployeeSheet.partTime", "Part-time")}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Contract Hours (Hours + Minutes) */}
                    <div className="text-sm">
                        <span className="text-neutral-700">{i18n.t("wf.addEmployeeSheet.contractHours", "Contract Hours")}</span>
                        <div className="grid grid-cols-2 gap-3 mt-1">
                            <label className="block">
                                <span className="text-xs text-neutral-500">
                                    {i18n.t("wf.addEmployeeSheet.hours", "Hours")}
                                </span>
                                <Input
                                    data-testid="add-employee-hours"
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    step={1}
                                    value={state.hours}
                                    onChange={(e) => {
                                        const n = parseInt0(e.target.value);
                                        if (Number.isNaN(n) || n < 0) return;
                                        set({ hours: n });
                                    }}
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs text-neutral-500">
                                    {i18n.t("wf.addEmployeeSheet.minutes", "Minutes")}
                                </span>
                                <Input
                                    data-testid="add-employee-minutes"
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    max={59}
                                    step={1}
                                    value={state.minutes}
                                    onChange={(e) => {
                                        const n = parseInt0(e.target.value);
                                        if (Number.isNaN(n) || n < 0 || n > 59)
                                            return;
                                        set({ minutes: n });
                                    }}
                                />
                            </label>
                        </div>
                    </div>

                    {/* Contract Period — read-only fixed */}
                    <div className="text-sm">
                        <span className="text-neutral-700">{i18n.t("wf.addEmployeeSheet.contractPeriod", "Contract Period")}</span>
                        <div
                            data-testid="add-employee-contract-period"
                            className="mt-1 rounded-md border border-neutral-200
                                        bg-neutral-50 px-3 py-2 text-neutral-900"
                        >
                            3 weeks
                        </div>
                    </div>

                    {/* Working Time Model */}
                    <div className="text-sm">
                        <span className="text-neutral-700">
                            {i18n.t("wf.addEmployeeSheet.workingTimeModel", "Working Time Model")}
                        </span>
                        <Select
                            value={state.workingTimeModel}
                            onValueChange={(v) =>
                                set({ workingTimeModel: v })}
                        >
                            <SelectTrigger
                                data-testid="add-employee-wtm">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="STANDARD"
                                                data-testid="opt-standard">
                                    {i18n.t("wf.addEmployeeSheet.standard", "Standard")}
                                </SelectItem>
                                <SelectItem value="AVERAGING"
                                                data-testid="opt-averaging">
                                    Tasoittumisjärjestelmä
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* TES */}
                    <div className="text-sm">
                        <span className="text-neutral-700">{i18n.t("wf.addEmployeeSheet.tes", "TES")}</span>
                        <Select
                            value={state.tesFamily}
                            onValueChange={(v) => set({ tesFamily: v })}
                        >
                            <SelectTrigger
                                data-testid="add-employee-tes">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="PAM_MARA"
                                                data-testid="opt-pam-mara">
                                    {i18n.t("wf.addEmployeeSheet.hospitalityIndustryPamMara", "Hospitality industry (PAM–MaRa)")}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* TES-B2C1B1 §6 — VV system (no silent default). */}
                    <div className="text-sm">
                        <span className="text-neutral-700">{i18n.t("wf.addEmployeeSheet.vvSystem", "VV system")}</span>
                        <Select
                            value={state.vvSystem}
                            onValueChange={(v) => set({ vvSystem: v })}
                        >
                            <SelectTrigger
                                data-testid="add-employee-vv-system">
                                <SelectValue placeholder={i18n.t("wf.addEmployeeSheet.notSet", "Not set")} />
                            </SelectTrigger>
                            <SelectContent>
                                {VV_SYSTEM_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}
                                        data-testid={`opt-vv-${o.value}`}>
                                        {o.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="mt-1.5 text-xs text-neutral-500"
                            data-testid="add-employee-vv-hint">
                            {!state.vvSystem
                                ? i18n.t("wf.addEmployeeSheet.vvSystemNotSetYouCan", "VV system not set. You can set it later in the employee's profile.")
                                : VV_UNSUPPORTED_SYSTEMS.has(state.vvSystem)
                                    ? i18n.t("wf.addEmployeeSheet.miseoneDoesNotCurrentlyCalculate", "MiseOne does not currently calculate this VV system.")
                                    : i18n.t("wf.addEmployeeSheet.selectTheVvSystemThatApplies", "Select the VV system that applies to this employee's Employment Terms.")}
                        </p>
                    </div>

                    {/* Payroll */}
                    <label className="flex items-center justify-between text-sm">
                        <span className="text-neutral-700">{i18n.t("wf.addEmployeeSheet.payrollIncluded", "Payroll Included")}</span>
                        <Switch
                            data-testid="add-employee-payroll"
                            checked={state.payrollIncluded}
                            onCheckedChange={(v) =>
                                set({ payrollIncluded: Boolean(v) })}
                        />
                    </label>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block text-sm">
                            <span className="text-neutral-700">{i18n.t("wf.addEmployeeSheet.startDate", "Start Date")}</span>
                            <Input
                                data-testid="add-employee-start-date"
                                type="date"
                                value={state.startDate}
                                onChange={(e) =>
                                    set({ startDate: e.target.value })}
                            />
                        </label>
                        <label className="block text-sm">
                            <span className="text-neutral-700">{i18n.t("wf.addEmployeeSheet.endDate", "End Date")}</span>
                            <Input
                                data-testid="add-employee-end-date"
                                type="date"
                                min={state.startDate || undefined}
                                value={state.endDate}
                                onChange={(e) =>
                                    set({ endDate: e.target.value })}
                            />
                        </label>
                    </div>

                    {/* Submit */}
                    <div className="pt-2 flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="add-employee-cancel"
                        >
                            {i18n.t("common.cancel", "Cancel")}
                        </Button>
                        <Button
                            onClick={submit}
                            disabled={!canSubmit}
                            data-testid="add-employee-save"
                        >
                            {pending ? i18n.t("common.saving", "Saving…") : i18n.t("common.save", "Save")}
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
